import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../NavBar/EnhancedDropdown.css";
import {
  Home,
  Building2,
  Package,
  Monitor,
  Cloud,
  Star,
  Globe,
  Smartphone,
  Lock,
  Video,
  Camera,
  Building,
  Key,
  RefreshCw,
  FolderOpen,
  Download,
  Waves,
  Rocket,
  FileText,
  Shield,
  MessageSquare,
  Mail,
  BookOpen,
  PlayCircle,
  Wifi,
  DoorOpen,
  Fingerprint,
  Users,
  Bell,
  Gauge,
  Zap,
  Radio,
  Eye,
  UserCheck,
  ArrowUpCircle,
  GitBranch,
  ChevronDown,
  ChevronRight,
  Layers,
} from "lucide-react";

// Icon mapping for dropdown items
const iconMap = {
  "🏢": <Building2 size={20} strokeWidth={2} />,
  "📦": <Package size={20} strokeWidth={2} />,
  "🖥️": <Monitor size={20} strokeWidth={2} />,
  "☁️": <Cloud size={20} strokeWidth={2} />,
  "⭐": <Star size={20} strokeWidth={2} />,
  "🌐": <Globe size={20} strokeWidth={2} />,
  "📱": <Smartphone size={20} strokeWidth={2} />,
  "🔐": <Lock size={20} strokeWidth={2} />,
  "📹": <Video size={20} strokeWidth={2} />,
  "🎥": <Camera size={20} strokeWidth={2} />,
  "🔒": <Lock size={20} strokeWidth={2} />,
  "🔄": <RefreshCw size={20} strokeWidth={2} />,
  "📂": <FolderOpen size={20} strokeWidth={2} />,
  "📲": <Download size={20} strokeWidth={2} />,
  "🌊": <Waves size={20} strokeWidth={2} />,
  "🚀": <Rocket size={20} strokeWidth={2} />,
  "📜": <FileText size={20} strokeWidth={2} />,
  "💬": <MessageSquare size={20} strokeWidth={2} />,
  "📧": <Mail size={20} strokeWidth={2} />,
  "📚": <BookOpen size={20} strokeWidth={2} />,
  "🎬": <PlayCircle size={20} strokeWidth={2} />,
  "🎫": <Key size={20} strokeWidth={2} />,
  "⬆️": <ArrowUpCircle size={20} strokeWidth={2} />,
  "🔀": <GitBranch size={20} strokeWidth={2} />,
  "🗂️": <Layers size={20} strokeWidth={2} />,
  "📄": <FileText size={20} strokeWidth={2} />,
  "📋": <FileText size={20} strokeWidth={2} />,
  "🗓️": <Monitor size={20} strokeWidth={2} />,
  "💾": <Monitor size={20} strokeWidth={2} />,
};

// Category-specific icon mapping based on product category names
const getCategoryIcon = (categoryName) => {
  const name = categoryName?.toLowerCase() || "";

  if (name.includes("controller") || name.includes("control panel"))
    return <Gauge size={20} strokeWidth={2} />;
  if (name.includes("reader") || name.includes("card reader"))
    return <Fingerprint size={20} strokeWidth={2} />;
  if (name.includes("lock") || name.includes("lockset"))
    return <Lock size={20} strokeWidth={2} />;
  if (name.includes("credential") || name.includes("card"))
    return <Key size={20} strokeWidth={2} />;
  if (name.includes("wireless") || name.includes("wifi"))
    return <Wifi size={20} strokeWidth={2} />;
  if (name.includes("door") || name.includes("gate"))
    return <DoorOpen size={20} strokeWidth={2} />;
  if (name.includes("intercom") || name.includes("video"))
    return <Video size={20} strokeWidth={2} />;
  if (name.includes("camera") || name.includes("surveillance"))
    return <Camera size={20} strokeWidth={2} />;
  if (name.includes("alarm") || name.includes("alert"))
    return <Bell size={20} strokeWidth={2} />;
  if (name.includes("power") || name.includes("supply"))
    return <Zap size={20} strokeWidth={2} />;
  if (name.includes("module") || name.includes("accessory"))
    return <Radio size={20} strokeWidth={2} />;
  if (name.includes("software") || name.includes("system"))
    return <Monitor size={20} strokeWidth={2} />;
  if (name.includes("biometric") || name.includes("fingerprint"))
    return <Fingerprint size={20} strokeWidth={2} />;
  if (name.includes("visitor") || name.includes("guest"))
    return <Users size={20} strokeWidth={2} />;
  if (name.includes("monitor") || name.includes("display"))
    return <Eye size={20} strokeWidth={2} />;
  if (name.includes("access") || name.includes("entry"))
    return <UserCheck size={20} strokeWidth={2} />;
  if (
    name.includes("migrate") ||
    name.includes("migration") ||
    name.includes("upgrade")
  )
    return <ArrowUpCircle size={20} strokeWidth={2} />;

  // Default icon for unknown categories
  return <Package size={20} strokeWidth={2} />;
};

// Renders a single dropdown item (leaf node)
const DropdownItem = ({
  item,
  isActive,
  onClick,
  isProductDropdown,
  hideDescription,
}) => (
  <div
    onClick={() => onClick(item)}
    className={`enhanced-dropdown-item ${isActive ? "active" : ""}`}
  >
    <div className="enhanced-dropdown-icon">
      {isProductDropdown && item.key !== "all"
        ? getCategoryIcon(item.label)
        : iconMap[item.icon] || item.icon}
    </div>
    <div className="enhanced-dropdown-text">
      <div className="enhanced-dropdown-label">{item.label}</div>
      {!hideDescription && item.description && (
        <div className="enhanced-dropdown-description">{item.description}</div>
      )}
    </div>
  </div>
);

// Renders a parent item with expandable children (tree node)
const ParentDropdownItem = ({
  item,
  currentPath,
  onChildClick,
  isProductDropdown,
}) => {
  const [expanded, setExpanded] = useState(false);

  const hasActiveChild = item.children?.some(
    (child) => child.link === currentPath
  );

  return (
    <div className="enhanced-dropdown-parent-wrapper">
      <div
        className={`enhanced-dropdown-item enhanced-dropdown-parent ${
          hasActiveChild ? "active" : ""
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="enhanced-dropdown-icon">
          {iconMap[item.icon] || item.icon}
        </div>
        <div className="enhanced-dropdown-text">
          <div className="enhanced-dropdown-label">{item.label}</div>
          {item.description && (
            <div className="enhanced-dropdown-description">
              {item.description}
            </div>
          )}
        </div>
        <div className="enhanced-dropdown-chevron">
          {expanded ? (
            <ChevronDown size={14} strokeWidth={2} />
          ) : (
            <ChevronRight size={14} strokeWidth={2} />
          )}
        </div>
      </div>
      {expanded && (
        <div className="enhanced-dropdown-children">
          {item.children?.map((child) => (
            <DropdownItem
              key={child.key}
              item={child}
              isActive={child.link === currentPath}
              onClick={onChildClick}
              isProductDropdown={isProductDropdown}
              hideDescription={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const EnhancedDropdown = ({
  items,
  label,
  isProductPage,
  currentPath,
  currentSearch,
  onDemoClick,
  isOpen: externalIsOpen,
  onOpenChange,
  isProductDropdown,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;

  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const handleItemClick = (item) => {
    if (item.action === "demo" && onDemoClick) {
      onDemoClick();
    } else if (item.external) {
      window.open(item.link, "_blank", "noopener noreferrer");
    } else {
      navigate(item.link);
    }
    setIsOpen(false);
  };

  const isActive = (item) => {
    if (currentSearch) {
      return item.link === `${currentPath}${currentSearch}`;
    }
    return item.link === currentPath;
  };

  // Calculate columns based on item count
  const itemsPerColumn = 5;
  const columnCount = Math.ceil(items.length / itemsPerColumn);

  // Split items into columns
  const columns = [];
  for (let i = 0; i < columnCount; i++) {
    columns.push(items.slice(i * itemsPerColumn, (i + 1) * itemsPerColumn));
  }

  return (
    <div ref={dropdownRef} className="enhanced-dropdown-container">
      <div className="enhanced-dropdown-trigger" onClick={handleClick}>
        {label}
      </div>

      {isOpen && (
        <div className="enhanced-dropdown-menu">
          <div className={`enhanced-dropdown-content columns-${columnCount}`}>
            {columns.map((column, columnIndex) => (
              <div key={columnIndex} className="enhanced-dropdown-column">
                {column.map((item) =>
                  item.isParent ? (
                    <ParentDropdownItem
                      key={item.key}
                      item={item}
                      currentPath={currentPath}
                      onChildClick={handleItemClick}
                      isProductDropdown={isProductDropdown}
                    />
                  ) : (
                    <DropdownItem
                      key={item.key}
                      item={item}
                      isActive={isActive(item)}
                      onClick={handleItemClick}
                      isProductDropdown={isProductDropdown}
                    />
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
